import React from 'react';
import { Button, Modal, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import SoftTag from '../components/SoftTag';
import { getQuoteStatusTone } from '../constants/statuses';
import { formatDate } from './formatters';
import { brandSemantic } from '../config/brand';

const LINE_TYPE_TONES = {
  Product: { color: brandSemantic.primary, background: brandSemantic.primarySoft, border: brandSemantic.primaryMuted },
  Service: { color: brandSemantic.info, background: brandSemantic.infoSoft, border: brandSemantic.infoBorder },
  Logistics: { color: brandSemantic.warning, background: brandSemantic.warningSoft, border: brandSemantic.warningBorder },
};

/**
 * Shared Quotes table columns, used both by the dedicated Quotes list page
 * and the Inquiry Detail page's Quotes tab, so a Quote looks the same
 * wherever it's listed: Quote ID, Customer, Product Category, Product,
 * Created Date, Status.
 */
export const buildQuotesColumns = ({
  t,
  leads = [],
  openQuoteDetail,
  openLeadProfile,
  returnView = 'quotes',
  canDelete,
}) => [
  {
    title: t('Quote ID'),
    dataIndex: 'id',
    key: 'id',
    render: (id, record) => (
      <Button type="link" onClick={() => openQuoteDetail(record, returnView)} style={{ padding: 0, fontWeight: 600 }}>
        {id}
      </Button>
    ),
  },
  {
    title: t('Client Name'),
    dataIndex: 'client',
    key: 'client',
    render: (text, record) => {
      const lead = leads.find((item) => item.id === record.clientId);
      return (
        <Button type="link" onClick={() => { if (lead) openLeadProfile?.(lead, returnView); }} style={{ padding: 0 }}>
          {text}
        </Button>
      );
    },
  },
  {
    title: t('Type'),
    dataIndex: 'lineType',
    key: 'lineType',
    width: 100,
    render: (value) => <SoftTag tone={LINE_TYPE_TONES[value || 'Product']}>{t(value || 'Product')}</SoftTag>,
  },
  {
    title: t('Product Category'),
    key: 'productCategory',
    width: 160,
    render: (_, record) => record.categoryName || record.productCategory || <span style={{ color: '#bfbfbf' }}>—</span>,
  },
  {
    title: t('Product'),
    dataIndex: 'productName',
    key: 'productName',
    render: (value) => value || <span style={{ color: '#bfbfbf' }}>—</span>,
  },
  {
    title: t('Created Date'),
    key: 'date',
    width: 140,
    render: (_, record) => formatDate((record.createdAt || record.date || '').slice(0, 10)),
  },
  {
    title: t('Status'),
    dataIndex: 'status',
    key: 'status',
    width: 170,
    render: (status) => <SoftTag tone={getQuoteStatusTone(status)}>{t(status)}</SoftTag>,
  },
  ...(canDelete ? [{
    title: t('Actions'),
    key: 'actions',
    width: 100,
    render: () => (
      <Button
        type="link"
        size="small"
        danger
        icon={<DeleteOutlined />}
        style={{ padding: 0, height: 'auto' }}
        onClick={() => {
          Modal.confirm({
            title: t('Delete'),
            content: t('Are you sure you want to delete this entry?'),
            onOk() { message.success(t('Deleted')); },
          });
        }}
      >
        {t('Delete')}
      </Button>
    ),
  }] : []),
];
